/**
 * Rivelazione parola per parola agganciata allo scroll (scrub).
 *
 * Le parole del blocco partono smorzate (opacity 0.3) e si accendono in
 * sequenza man mano che si scorre: la progressione è legata alla posizione di
 * scroll, non al tempo, quindi il lettore "trascina" la lettura. Una volta
 * accesa per intero la frase resta accesa: risalendo non torna smorzata.
 * Riferimento: gsap-text-animator.webflow.io.
 *
 * Finestra di scroll (diversa dallo snippet di riferimento, che con start
 * 'top center' e l'end di default 'bottom top' completava la lettura con il
 * testo già fuori dal viewport):
 *
 *  - start 'top 85%': parte appena la frase entra, stesso punto di ingresso di
 *    reveal.js e mark-highlight.js.
 *  - end 'bottom 60%': l'ultima parola si accende con il fondo della frase
 *    appena sotto il centro, cioè con il testo ancora in piena zona di lettura.
 *  - clamp() su entrambi: su un settore senza sezioni sotto l'intro la pagina
 *    può finire prima che il fondo della frase arrivi al 60%, e senza clamp la
 *    frase resterebbe accesa a metà.
 *  - scrub 0.4 (come timeline.js): smussa gli scatti di rotella e trackpad
 *    senza un ritardo percepibile.
 *  - once: a fine corsa ScrollTrigger si rimuove ma lascia finire lo scrub
 *    tween, quindi la frase arriva comunque a piena opacità. Prima della fine
 *    il reveal resta reversibile.
 *
 * Aggancio: qualsiasi blocco con [data-word-reveal] — oggi la frase chiave del
 * settore (.settore-intro__keyphrase, template-parts/settore/intro.php).
 *
 * Scelte tecniche rispetto allo snippet di riferimento:
 *
 *  - SplitText al posto di split-type: è già dentro il pacchetto gsap 3.15
 *    (gratuito dalla 3.13), quindi zero dipendenze nuove e zero <script> da CDN,
 *    coerente con il bundle Rollup del tema.
 *
 *  - type: 'words' invece di 'lines, words, chars'. Lo snippet originale splitta
 *    anche righe e caratteri ma poi anima solo .word: nodi DOM sprecati. Qui la
 *    differenza è anche funzionale — SplitText applica display:inline-block ai
 *    nodi annidati solo nello split di soli caratteri, e i wrapper di riga sono
 *    display:block. Con 'words' + tag:'span' lo split resta completamente neutro
 *    sul layout: nessun display forzato, nessun ricalcolo delle andate a capo.
 *    È ciò che permette al <mark> dell'evidenziatore ([u]…[/u], vedi
 *    mark-highlight.js) di continuare a spezzarsi su più righe quando la frase
 *    chiave lo contiene.
 *
 *  - tag: 'span' è obbligatorio: il default di SplitText è 'div', non valido
 *    dentro il <p> generato dalla WYSIWYG.
 *
 * Accessibilità: aria:'auto' (default) mette aria-label con la frase intera sul
 * contenitore e aria-hidden sui frammenti, così gli screen reader leggono il
 * testo continuo e non parola per parola.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

( function () {
	'use strict';

	const blocks = document.querySelectorAll( '[data-word-reveal]' );
	if ( ! blocks.length ) {
		return;
	}

	if ( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return; // testo pienamente leggibile, nessuno split e nessuna animazione.
	}

	gsap.registerPlugin( ScrollTrigger, SplitText );

	blocks.forEach( ( block ) => {
		const split = new SplitText( block, {
			type: 'words',
			tag: 'span',
			wordsClass: 'am-word',
		} );

		if ( ! split.words.length ) {
			return;
		}

		gsap.from( split.words, {
			opacity: 0.3,
			duration: 0.5,
			ease: 'power1.out',
			stagger: 0.15,
			scrollTrigger: {
				trigger: block,
				start: 'clamp(top 85%)',
				end: 'clamp(bottom 60%)',
				scrub: 0.4,
				once: true,
			},
		} );
	} );
}() );
